// @ts-nocheck
/*
 * Port of views/dashboard/disconnected-calls-summary.js.
 * API: API_DASHBOARD_DISCONNECTED_CALL_SUMMARY (single GET). Byte-identical:
 *   - default range: first day of month → today
 *   - 3-month lookback cap (toast on violation)
 *   - CALL_CENTRE_AGENT early return
 *   - Preserves exact toast strings: "Start and End date required",
 *     "Start date cannot be after end date",
 *     "Date range cannot go beyond the last 3 months"
 * Card count: 3 tiles (Low Balance, Insufficient Channel Licence,
 * Out of Time Schedule) — the 4th is commented out in the original,
 * do not re-enable.
 */
import { useEffect, useState } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Clock, Sliders, Wallet } from 'lucide-react';
import { getApiCall } from '@/service/httpCall';
import { API_DASHBOARD_DISCONNECTED_CALL_SUMMARY } from '@/service/url';
import { USER_ROLES } from '@/utilities/constant';
import { Storage } from '@/utilities/setting';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';

const firstDayOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
};
const todayStr = () => new Date().toISOString().split('T')[0];
const threeMonthsAgoStr = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 3);
  return d.toISOString().split('T')[0];
};

function StatCard({ title, value, icon }) {
  return (
    <div className="flex items-center gap-3 shadow-neu-raised rounded-lg bg-card p-4 transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:scale-[1.02]">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-muted-foreground">{title}</p>
        <h2 className="text-lg font-bold text-foreground">{value}</h2>
      </div>
    </div>
  );
}

export default function DisconnectedCallsSummary() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = usePersistedState('dashboard/DisconnectedCallsSummary:filters', {
    startDate: firstDayOfMonth(),
    endDate: todayStr(),
  });
  const [loggedInUserRole, setLoggedInUserRole] = useState(null);

  useEffect(() => {
    const rawUser = Storage.getItem('user');
    if (!rawUser) {
      console.error('User not found in Storage');
      return;
    }
    try {
      const user = JSON.parse(rawUser);
      if (user.role) setLoggedInUserRole(user.role);
    } catch (e) {
      console.error('Invalid JSON in user from Storage', e);
    }
  }, []);

  useEffect(() => {
    if (!loggedInUserRole) return;
    if (loggedInUserRole == USER_ROLES.CALL_CENTRE_AGENT) return;
    fetchData();

  }, [loggedInUserRole]);

  const fetchData = () => {
    if (!filters.startDate || !filters.endDate) {
      toast.error('Start and End date required');
      return;
    }
    if (filters.startDate > filters.endDate) {
      toast.error('Start date cannot be after end date');
      return;
    }
    if (filters.startDate < threeMonthsAgoStr()) {
      toast.error('Date range cannot go beyond the last 3 months');
      return;
    }
    setLoading(true);
    const query = `${API_DASHBOARD_DISCONNECTED_CALL_SUMMARY}?startDate=${filters.startDate}&endDate=${filters.endDate}`;
    getApiCall(query)
      .then((resp) => setSummary(resp?.data || resp))
      .catch((err) => toast.error(friendlyApiError(err) || 'Error fetching disconnected call summary'))
      .finally(() => setLoading(false));
  };

  const clearFilters = () => {
    setFilters({ startDate: firstDayOfMonth(), endDate: todayStr() });
    fetchData();
  };

  if (loading) {
    return (
      <Card className="mb-6 p-6 text-center text-sm text-muted-foreground">
        Loading disconnected calls…
      </Card>
    );
  }

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-bold text-foreground">Disconnected Calls</span>
      </div>

      <DateRangeFilter
        compact
        className="mb-4"
        fromLabel="Start Date"
        toLabel="End Date"
        value={{ from: filters.startDate, to: filters.endDate }}
        min={threeMonthsAgoStr()}
        max={todayStr()}
        onChange={({ from, to }) => setFilters({ ...filters, startDate: from, endDate: to })}
        onApply={fetchData}
        applyLabel="Search"
        onReset={clearFilters}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
        <StatCard title="Low Balance" value={summary?.lowBalance ?? 0} icon={<Wallet className="h-4 w-4" />} />
        <StatCard
          title="Insufficient Channel Licence"
          value={summary?.channelLicence ?? 0}
          icon={<Sliders className="h-4 w-4" />}
        />
        <StatCard
          title="Out of Time Schedule"
          value={summary?.outOfTimeSchedule ?? 0}
          icon={<Clock className="h-4 w-4" />}
        />
        {/* Commented-out in JS source — DO NOT re-enable:
            <StatCard title="Total Disconnected" value={summary?.total ?? 0} ... /> */}
      </div>
    </>
  );
}
